import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { useTheme } from '../contexts/ThemeContext';
import 'highlight.js/styles/github.css';
import 'highlight.js/styles/github-dark.css';

const DocumentationEditor = ({ 
  initialDocumentation = '',
  initialCategory = '',
  initialTags = '',
  initialIconAlt = '',
  onSave,
  onCancel,
  title = 'Documentation Editor',
  showIconFields = true
}) => {
  const { isDarkMode } = useTheme();
  const [documentation, setDocumentation] = useState(initialDocumentation);
  const [category, setCategory] = useState(initialCategory);
  const [tags, setTags] = useState(initialTags);
  const [iconAlt, setIconAlt] = useState(initialIconAlt);
  const [previewMode, setPreviewMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setDocumentation(initialDocumentation);
    setCategory(initialCategory);
    setTags(initialTags);
    setIconAlt(initialIconAlt);
  }, [initialDocumentation, initialCategory, initialTags, initialIconAlt]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({
        documentation,
        category: category.trim(),
        tags: tags.trim(),
        iconAlt: iconAlt.trim()
      });
    } catch (error) {
      console.error('Error saving documentation:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };


  return (
    <div className="card" style={{ 
      backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
      border: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`,
      borderRadius: '8px',
      padding: '1.5rem',
      marginBottom: '1rem'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1.5rem',
        paddingBottom: '1rem',
        borderBottom: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`
      }}>
        <h2 style={{ 
          margin: 0, 
          color: isDarkMode ? 'white' : '#333',
          fontSize: '1.5rem'
        }}>
          {title}
        </h2>
        
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className={`btn ${previewMode ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setPreviewMode(!previewMode)}
            style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
          >
            {previewMode ? 'Edit' : 'Preview'}
          </button>
        </div>
      </div>

      {/* Form Fields */}
      {!previewMode && (
        <div style={{ marginBottom: '1.5rem' }}>
          {/* Category */}
          <div className="form-group" style={{ marginBottom: '1rem' }}>
            <label className="form-label" htmlFor="category">
              Category
            </label>
            <input
              type="text"
              id="category"
              className="form-input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g., Bioinformatics, Data Analysis, Visualization"
              style={{
                backgroundColor: isDarkMode ? '#3a3a3a' : 'white',
                color: isDarkMode ? 'white' : '#333',
                border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`
              }}
            />
          </div>

          {/* Tags */}
          <div className="form-group" style={{ marginBottom: '1rem' }}>
            <label className="form-label" htmlFor="tags">
              Tags (comma-separated)
            </label>
            <input
              type="text"
              id="tags"
              className="form-input"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="e.g., genomics, python, analysis, tools"
              style={{
                backgroundColor: isDarkMode ? '#3a3a3a' : 'white',
                color: isDarkMode ? 'white' : '#333',
                border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`
              }}
            />
            <small className="form-text" style={{ color: isDarkMode ? '#aaa' : '#666' }}>
              Separate multiple tags with commas
            </small>
          </div>

          {/* Icon Alt Text */}
          {showIconFields && (
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label" htmlFor="iconAlt">
                Icon Alt Text
              </label>
              <input
                type="text"
                id="iconAlt"
                className="form-input"
                value={iconAlt}
                onChange={(e) => setIconAlt(e.target.value)}
                placeholder="Descriptive text for the icon"
                style={{
                  backgroundColor: isDarkMode ? '#3a3a3a' : 'white',
                  color: isDarkMode ? 'white' : '#333',
                  border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`
                }}
              />
            </div>
          )}

          {/* Documentation Editor */}
          <div className="form-group">
            <label className="form-label" htmlFor="documentation">
              Documentation (Markdown)
            </label>
            <textarea
              id="documentation"
              className="form-input"
              value={documentation}
              onChange={(e) => setDocumentation(e.target.value)}
              placeholder="Write your documentation in Markdown format..."
              rows="20"
              style={{
                backgroundColor: isDarkMode ? '#3a3a3a' : 'white',
                color: isDarkMode ? 'white' : '#333',
                border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                fontSize: '0.875rem',
                lineHeight: '1.5'
              }}
            />
            <small className="form-text" style={{ color: isDarkMode ? '#aaa' : '#666' }}>
              Supports Markdown syntax including code blocks, tables, and more
            </small>
          </div>
        </div>
      )}

      {/* Preview Mode */}
      {previewMode && (
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ 
            backgroundColor: isDarkMode ? '#1a1a1a' : '#f8f9fa',
            border: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`,
            borderRadius: '8px',
            padding: '1.5rem',
            maxHeight: '500px',
            overflow: 'auto'
          }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeHighlight]}
              components={{
                h1: ({ children }) => (
                  <h1 style={{ 
                    color: isDarkMode ? 'white' : '#333',
                    borderBottom: `2px solid ${isDarkMode ? '#555' : '#e9ecef'}`,
                    paddingBottom: '0.5rem',
                    marginBottom: '1rem'
                  }}>
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 style={{ 
                    color: isDarkMode ? 'white' : '#333',
                    borderBottom: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`,
                    paddingBottom: '0.25rem',
                    marginTop: '1.5rem',
                    marginBottom: '0.75rem'
                  }}>
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 style={{ 
                    color: isDarkMode ? 'white' : '#333',
                    marginTop: '1.25rem',
                    marginBottom: '0.5rem'
                  }}>
                    {children}
                  </h3>
                ),
                code: ({ children, className }) => {
                  const isInline = !className;
                  return (
                    <code
                      style={{
                        backgroundColor: isDarkMode ? '#3a3a3a' : '#f8f9fa',
                        color: isDarkMode ? '#e0e0e0' : '#333',
                        padding: isInline ? '0.2rem 0.4rem' : '1rem',
                        borderRadius: isInline ? '4px' : '8px',
                        fontSize: isInline ? '0.9em' : '0.875rem',
                        fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                        display: isInline ? 'inline' : 'block',
                        overflow: 'auto'
                      }}
                      className={className}
                    >
                      {children}
                    </code>
                  );
                },
                pre: ({ children }) => (
                  <pre style={{
                    backgroundColor: isDarkMode ? '#1a1a1a' : '#f8f9fa',
                    border: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`,
                    borderRadius: '8px',
                    padding: '1rem',
                    overflow: 'auto',
                    margin: '1rem 0'
                  }}>
                    {children}
                  </pre>
                ),
                blockquote: ({ children }) => (
                  <blockquote style={{
                    borderLeft: `4px solid ${isDarkMode ? '#555' : '#e9ecef'}`,
                    paddingLeft: '1rem',
                    margin: '1rem 0',
                    fontStyle: 'italic',
                    color: isDarkMode ? '#ccc' : '#666'
                  }}>
                    {children}
                  </blockquote>
                )
              }}
            >
              {documentation || '*No content to preview*'}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        gap: '0.75rem',
        paddingTop: '1rem',
        borderTop: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`
      }}>
        <button
          className="btn btn-secondary"
          onClick={handleCancel}
          disabled={isSaving}
          style={{ padding: '0.75rem 1.5rem' }}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={isSaving}
          style={{ padding: '0.75rem 1.5rem' }}
        >
          {isSaving ? 'Saving...' : 'Save Documentation'}
        </button>
      </div>
    </div>
  );
};

export default DocumentationEditor;
